import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { SelectGroup } from './select-panel.types';
import { containsAll } from '@core/lib/helpers/helpers';

@Component({
  selector: 'wfm-select-panel',
  templateUrl: './select-panel.component.html',
  styleUrls: ['./select-panel.component.scss']
})
export class SelectPanelComponent implements OnChanges {
  @Input() items: Array<SelectGroup | any> = [];

  @Input() itemTemplate?: TemplateRef<any>;

  @Input() placeholder = '';

  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();

  @Input() labelField = '';

  @Input() iconField = '';

  @Input() maxSelection = 0;

  @Input() columns = 0;

  @Input() multiple = false;

  @Input() required = false;

  @Input() search = false;
  @Input() searchLabel = 'core.label.search';

  @Input() enableInternalSearch = true;
  @Output() queryStr = new EventEmitter<any>();

  @Input() highlightSelected = false;

  @Input() grouping = false;

  @Input() dateRangeOptions = false;

  @Input() selectAllOption = false;

  @Input() unsetMaxHeight = false;

  @Input() translationEnabled = true;

  query = '';

  filteredItems: Array<SelectGroup | any>;

  @ViewChild('panelScroll') panelScroll: ElementRef;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLElement>;

  isSelectedAll = false;
  setSelectedAll(): boolean {
    if (!this.highlightSelected) {
      this.isSelectedAll = !!(
        this.multiple &&
        this.filteredItems?.length &&
        this.value?.length >= this.filteredItems.length &&
        containsAll(this.value, this.filteredItems)
      );
    } else {
      this.isSelectedAll = !!(this.multiple && this.filteredItems.length < 1 && this.value?.length >= 0);
    }

    if (!this.value?.length) {
      this.isSelectedAll = false;
    }

    return this.isSelectedAll;
  }

  get isListOfObjects(): boolean {
    if (this.grouping) {
      return typeof (this.items[0] as SelectGroup).items[0] === 'object';
    }
    return typeof this.items[0] === 'object';
  }

  get itemStyle(): any {
    if (this.columns > 0 && !this.grouping) {
      return {
        'min-width': Math.round(100 / this.columns) + '%',
        float: 'left'
      };
    }

    return {};
  }

  get flatItems(): any[] {
    if (this.grouping) {
      return this.items.reduce((value, item) => (value as Array<any>).concat(item.items), []);
    }
    return this.items;
  }

  private window: Window;

  constructor(@Inject(DOCUMENT) private document) {
    this.window = this.document.defaultView;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items || changes.search || changes.value) {
      this.updateFilteredItems();
    }
  }

  itemClick(item: any) {
    this.window.setTimeout(() => {
      if (this.multiple) {
        if (!this.value) {
          this.value = [];
        }

        const index = this.value.indexOf(item);
        if (index > -1) {
          (this.value as any[]).splice(index, 1);
        } else {
          this.value.push(item);
        }
      } else {
        this.value = item;
      }

      this.updateFilteredItems();

      this.valueChange.emit(this.value);
    }, 0);
  }

  toggleSelectAll() {
    this.value = this.value?.filter(item => {
      return !this.filteredItems?.includes(item);
    });

    if (!this.isSelectedAll) {
      this.value = [...(this.value || []), ...(this.filteredItems || [])];
    }

    if (this.highlightSelected && this.isSelectedAll) {
      this.value = [];
    }

    this.updateFilteredItems();
    this.valueChange.emit(this.value);
  }

  updateFilteredItems() {
    let items = this.items || [];

    if (this.highlightSelected) {
      items = this.filterNotSelectedItems();
    }

    if (!this.search || !this.query) {
      this.filteredItems = items;
    } else {
      this.filteredItems = this.filterItems(items);
    }
    this.setSelectedAll();
  }

  querySearchStr() {
    this.queryStr.emit(this.query);
  }

  removeFilter(): void {
    this.query = '';
    this.updateFilteredItems();
  }

  scroll() {
    if (this.panelScroll && !this.highlightSelected) {
      const panel: HTMLElement = this.panelScroll.nativeElement;
      const firstSelected: HTMLElement = panel.querySelector('.select__option--selected');

      if (firstSelected && firstSelected.offsetTop > panel.offsetHeight) {
        panel.scroll(0, firstSelected.offsetTop);
      }
    }
    this.searchInput?.nativeElement?.focus();
  }

  private filterNotSelectedItems(): any[] {
    const value = this.value || [];

    if (this.grouping) {
      const items = [];

      for (const group of this.items) {
        items.push({
          label: (group as SelectGroup).label,
          items: (group as SelectGroup).items.filter(item => {
            return !value.find(v => v === item);
          })
        });
      }

      return items;
    }

    return this.items.filter(item => {
      return !value.find(v => v === item);
    });
  }

  private filterItems(items: any[]): any[] {
    if (this.grouping) {
      const groups = [];
      for (const group of items) {
        const filteredItems = group.items.filter(
          item =>
            (this.isListOfObjects ? item[this.labelField] : item).toLowerCase().indexOf(this.query.toLowerCase()) >= 0
        );
        if (filteredItems.length > 0) {
          groups.push({
            label: group.label,
            items: filteredItems
          });
        }
      }

      return groups;
    }

    return items.filter(
      item => (this.isListOfObjects ? item[this.labelField] : item).toLowerCase().indexOf(this.query.toLowerCase()) >= 0
    );
  }
}
